/**
 * QP-208 — fold the cost snapshot's rolling yearly series into an aggregated
 * "after N years" cost-effect slice (MiFID II holding-period disclosure).
 *
 * The rolling series is per-period: `depositAmount`, `returnAmount` and
 * `totalCost` are sums for that single year, while `accountValue`,
 * `accountValueExCost` and `costEffectPct` are cumulative point-in-time values
 * at the end of the period. Folding periods 1..N therefore sums the per-period
 * fields and takes the point-in-time fields from entry N.
 *
 * Shared by the web cost-table widget and the PDF cost-table renderer so the
 * two surfaces can never drift.
 */

/**
 * How many years the cost simulation always runs. Components decide how many of
 * these years they DISPLAY (cost chart projectionYears, cost table
 * holdingPeriodYears) — changing a display setting never requires recalculation.
 */
export const COST_SIMULATION_YEARS = 20;

/** Structural subset of the snapshot's YearlyCostEntry that the fold needs. */
export type CostPeriodEntry = {
  accountValue: number;
  accountValueExCost: number;
  depositAmount?: number;
  returnAmount?: number;
  totalCost?: number;
  /** Cumulative reduction-in-return % at the END of this period. */
  costEffectPct?: number;
};

export type HoldingPeriodCost = {
  /** Actual number of years folded (min of requested years and series length). */
  years: number;
  depositAmount: number;
  accountValueExCost: number;
  accountValue: number;
  costEffectPct: number;
  returnAmount: number;
  returnAmountPct: number;
  totalCost: number;
};

/**
 * Fold rolling periods 1..years into one cumulative slice.
 *
 * Returns null when the series is missing, shorter than 2 periods (a 1-year
 * horizon collapses into the year-1 figures), or predates QP-208 (entries
 * without the per-period cost-effect fields).
 */
export function foldCostPeriods(
  entries: CostPeriodEntry[] | undefined,
  years: number,
): HoldingPeriodCost | null {
  if (!entries || entries.length < 2 || years < 2) return null;

  const slice = entries.slice(0, Math.min(years, entries.length));
  const last = slice[slice.length - 1];
  if (!last || slice.some((e) => e.depositAmount === undefined)) return null;

  const depositAmount = slice.reduce((sum, e) => sum + (e.depositAmount ?? 0), 0);
  const returnAmount = slice.reduce((sum, e) => sum + (e.returnAmount ?? 0), 0);
  const totalCost = slice.reduce((sum, e) => sum + (e.totalCost ?? 0), 0);

  const r2 = (n: number) => Math.round(n * 100) / 100;

  return {
    years: slice.length,
    depositAmount: r2(depositAmount),
    accountValueExCost: r2(last.accountValueExCost),
    accountValue: r2(last.accountValue),
    costEffectPct: r2(
      last.costEffectPct ??
        (last.accountValueExCost > 0
          ? ((last.accountValueExCost - last.accountValue) / last.accountValueExCost) * 100
          : 0),
    ),
    returnAmount: r2(returnAmount),
    returnAmountPct: r2(depositAmount > 0 ? (returnAmount / depositAmount) * 100 : 0),
    totalCost: r2(totalCost),
  };
}
